import { getElectronUrl, isElectronApp } from '#~/electron/utils.js';
import { useEffectOnce } from '@studio/react-utils/hooks';
import { getElectronAuthRedirectTarget } from './auth-redirect.js';
import { ElectronLoginProgress } from './login.jsx';
import { useMemo } from 'react';

/**
 * The desktop return page. Auth0 sends the desktop client's authorization code
 * here, and the page's only job is to hand it to the app over
 * `iris://code/<code>` (or `iris://auth-error?…`).
 *
 * It does NOT start `/authorize`. Both desktop entry points build that URL in
 * the main process — `getLoginUrl` and `getRegistrationUrl` in
 * `studio/electron/src/main/auth/utils.ts` — so there is one builder, and it is
 * the one that also owns the `redirect_uri` the app later sends at exchange
 * time. A page-side `/authorize` would have to be kept in step with it, and an
 * SDK-built one adds `state` and PKCE, neither of which the desktop can honour.
 *
 * Per rule code-comments-required (ai.knowledge/global).
 */
export const AppAuth = () => {
  const redirectTarget = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const code = searchParams.get('code') ?? undefined;
    const error = searchParams.get('error') ?? undefined;
    const errorDescription = searchParams.get('error_description') ?? undefined;

    return getElectronAuthRedirectTarget({
      ...(code ? { code } : {}),
      ...(error ? { error } : {}),
      ...(errorDescription ? { errorDescription } : {})
    });
  }, []);

  useEffectOnce(() => {
    if (!redirectTarget || isElectronApp()) {
      return;
    }

    window.open(getElectronUrl(redirectTarget), '_self');
  });

  // With no result in the query there is nothing to hand over: the progress UI
  // stands, and its "Open Iris" button stays hidden until a code arrives.
  return <ElectronLoginProgress redirectTarget={redirectTarget} />;
};
